import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import AuditabilityGroup from '../../components/Auditability/AuditabilityGroup';
import { generateString } from './Dashboard';
import { useAuditData } from '../../shared/hooks/queries/useAudit';

// Count all tests recursively
const countAllTests = (groups: any[]): { passed: number; failed: number } => {
    let passed = 0;
    let failed = 0;

    const countGroup = (group: any) => {
        group.checks.forEach((test: any) => {
            if (test.is_valid) passed++;
            else failed++;
        });
        group.subgroups.forEach(countGroup);
    };

    groups.forEach(countGroup);
    return { passed, failed };
};

export default function Audit() {
    const { model, currentScenario, currentSensitivity, currentEntity, computeGeneration, contextFirstError, contextSensitivityErrorCount } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }

    return <AuditContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} computeGeneration={computeGeneration} contextFirstError={contextFirstError} contextSensitivityErrorCount={contextSensitivityErrorCount} />;
}

// Content component: business logic and rendering
function AuditContent({ currentEntity, model, currentScenario, currentSensitivity, computeGeneration, contextFirstError, contextSensitivityErrorCount }) {
    // Check if the current entity has errors
    const hasErrors = contextFirstError || (currentSensitivity && contextSensitivityErrorCount > 0);

    const { data: auditData = [], isLoading: loading } = useAuditData(
        currentEntity?.id ?? 0,
        currentEntity?.type ?? 'scenario',
        computeGeneration,
    );

    // Calculate summary stats
    const { passed, failed } = useMemo(() => countAllTests(auditData), [auditData]);
    const total = passed + failed;
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
    const allPassed = failed === 0 && total > 0;

    return (
        <div style={{ height: "100%", overflow: "auto" }}>
            <div className="flex flex-col h-full">
                <FMEPageHeader
                    title="Audit"
                    breadcrumb={[model.name]}
                    description={
                        loading
                            ? "Loading audit data..."
                            : `${auditData.length} ${auditData.length === 1 ? 'test group.' : 'test groups.'}`
                    }
                    actions={<ScenarioSensitivitySelector />}
                />

                <motion.div
                    className="px-8 py-6"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.16 } }}
                >
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500">Loading audit data...</p>
                        </div>
                    </div>
                ) : hasErrors ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-[32px] text-slate-400">fact_check</span>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No Audit Data</h3>
                        <p className="text-slate-500 max-w-md">
                            Please complete all required inputs in the Architecture section.
                        </p>
                    </div>
                ) : auditData.length > 0 ? (
                    <div className="space-y-6">
                        {/* Summary Card */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                            <div className="flex flex-wrap items-center gap-6">
                                {/* Status indicator */}
                                <div className="flex items-center gap-3">
                                    <div className={`size-12 rounded-full flex items-center justify-center ${allPassed ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                        <span className={`material-symbols-outlined text-[28px] ${allPassed ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {allPassed ? 'check_circle' : 'error'}
                                        </span>
                                    </div>
                                    <div>
                                        <p className={`text-lg font-bold ${allPassed ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {allPassed ? 'All Tests Passed' : 'Issues Found'}
                                        </p>
                                        <p className="text-sm text-slate-500">{passRate}% pass rate</p>
                                    </div>
                                </div>

                                <div className="h-10 w-px bg-slate-200 hidden sm:block" />

                                {/* Stats */}
                                <div className="flex gap-6">
                                    <div className="flex items-center gap-2">
                                        <div className="size-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[18px] text-emerald-600">check</span>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-slate-900">{passed}</p>
                                            <p className="text-xs text-slate-500">Passed</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="size-8 rounded-lg bg-red-100 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[18px] text-red-600">close</span>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-slate-900">{failed}</p>
                                            <p className="text-xs text-slate-500">Failed</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="size-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-[18px] text-slate-600">summarize</span>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-slate-900">{total}</p>
                                            <p className="text-xs text-slate-500">Total</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Info text */}
                            <div className="mt-4 pt-4 border-t border-slate-100">
                                <p className="text-sm text-slate-600">
                                    These tests are key indicators for the consistency of the model.
                                    <span className="text-slate-400 ml-1">Tests are carried out with a certain degree of tolerance.</span>
                                </p>
                            </div>
                        </div>

                        {/* Test Groups */}
                        <div>
                            {auditData.map(auditGroup => (
                                <AuditabilityGroup
                                    key={generateString(10)}
                                    group={auditGroup}
                                    identifier={generateString(10)}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-[32px] text-slate-400">fact_check</span>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No Audit Data</h3>
                        <p className="text-slate-500 max-w-md">
                            Please design your architecture and complete all required inputs to see the computed results.
                        </p>
                    </div>
                )}
            </motion.div>
            </div>
        </div>
    );
}
